import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
// import { scaleQuantize } from 'd3-scale';
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import axios from "axios";
import Swal from "sweetalert2";
import { AUTH_URL, CHAT_URL } from "../../utils/constants";
// import { truncateText } from '../../utils/utils';
import TruncatedText from "../../utils/TruncatedText";
import { toCamelCase, toSentenceCase, getOfficerGrievanceStatus, stripMarkdown } from "../../utils/utils";
import PieCharts from "../dashboard/PieCharts";
import upDownArrow from "../../assets/images/up-down-arrow.svg";
import { getGrievance } from "../adminDashboard/AdminUser";
import AiSummaryModal from "../commonComponent/AiSummaryModal";
import FormattedMarkdown from "../commonComponent/FormattedMarkdown";
import { downloadBtrPdf, getBtrText } from "../../utils/btrPdf";
const GRADIENT_COLORS = ["#FF852B", "#D37929", "#B34700"];

export default function DashboardAdmin({
  user,
  dashboardData,
  data,
  setQueryUpdate,
  overdueData,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showCommentArea, setShowCommentArea] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [comment, setComment] = useState(""); // New state to store comment
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [atrFile, setAtrFile] = useState(null);
  const [atrUploadLoading, setAtrUploadLoading] = useState(false);
  const [atrSummary, setAtrSummary] = useState("");
  const [atrFileUrl, setAtrFileUrl] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploadLoading, setAudioUploadLoading] = useState(false);
  const [pieChartData, setPieChartData] = useState(null);
  const [audioSummary, setAudioSummary] = useState("");
  const [audioFileUrl, setAudioFileUrl] = useState("");
  const [response, setResponse] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParam, setSearchParam] = useState("All");
  const [transfer, setTransfer] = useState(false);
  const [transferAdmin, setTransferAdmin] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [officerComments, setOfficerComments] = useState("");
  // Draft of the "Comment from officer" field in the complaint modal. Kept
  // separate from officerComments, which is the transfer/escalation reason.
  const [commentDraft, setCommentDraft] = useState("");
  const [tranferDept, setTransferDept] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchVal = params.get("search");
    if (searchVal) {
      setSearchQuery(searchVal);
    }
  }, [location.search]);

  useEffect(() => {
    // Populate the form using existing logic
    if (selectedRow) {
      if (selectedRow.officer_grievance_status === "Add Comment") {
        setOfficerComments("");
      } else {
        setOfficerComments(selectedRow.officer_grievance_comments || "");
      }
    }

    // Fetch dynamic departments for the transfer dropdown
    const fetchDepartments = async () => {
      try {
        const res = await axios.get(`${AUTH_URL.replace('/api/auth/', '/api/')}officers`);
        if (res.data?.success) {
          const allOfficers = res.data.data;
          const userDept = (user?.category || "").trim();
          
          // Get unique departments excluding current user's department
          const uniqueDepts = [...new Set(allOfficers.map(o => (o.cag_category || "").trim()))].filter(
            dept => dept && dept !== userDept
          );
          setAvailableDepartments(uniqueDepts);
        }
      } catch (err) {
        console.error("Failed to fetch available departments:", err);
      }
    };
    fetchDepartments();
  }, [selectedRow, user]);

  // Red bell modal state
  const [bellModalRow, setBellModalRow] = useState(null);
  const [isBellModalOpen, setIsBellModalOpen] = useState(false);

  // BTR state
  const [btrMode, setBtrMode] = useState(null); // null | 'generate' | 'upload'
  const [btrContent, setBtrContent] = useState("");
  const [btrFile, setBtrFile] = useState(null);
  const [btrFileUrl, setBtrFileUrl] = useState("");
  const [btrSummary, setBtrSummary] = useState("");
  const [btrLoading, setBtrLoading] = useState(false);
  const [btrSubmitLoading, setBtrSubmitLoading] = useState(false);
  const [btrTab, setBtrTab] = useState("preview");

  // AI Summary state
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [aiSummaryText, setAiSummaryText] = useState("");
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const calculateDaysLapsed = (row) => {
    if (!row.grievance_submission_timestamp) return 0;
    const submittedDate = new Date(row.grievance_submission_timestamp);
    const date = new Date();
    const diff = date - submittedDate;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (row.tat === "within 1 day") return days - 1 > 0 ? days - 1 : 0;
    if (row.tat === "2 days") return days - 2 > 0 ? days - 2 : 0;
    if (row.tat === "3 days") return days - 3 > 0 ? days - 3 : 0;
    return 0;
  };
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Derive counts and groupings
  const stats = useMemo(() => {
    const normalizeStatus = (status) => {
      const s = (status || "pending").toLowerCase();

      if (s === "pending" || s === "open") return "Pending";
      if (s === "resolved" || s === "closed") return "Resolved";
      if (s.includes("rejected")) return "Rejected";
      if (s === "responded" || s.includes("btr") || s.includes("atr")) return "Responded";

      return "Other";
    };

    const statusCounts = {
      Pending: 0,
      Responded: 0,
      Resolved: 0,
      Rejected: 0,
      Other: 0,
    };

    const categories = {};
    const grievances = [];

    data.forEach((item) => {
      const actualStatus = getOfficerGrievanceStatus(item);
      const normalizedStatus = normalizeStatus(actualStatus);

      statusCounts[normalizedStatus] =
        (statusCounts[normalizedStatus] || 0) + 1;

      const category = item.llm_subcategory || "Other";

      categories[category] = (categories[category] || 0) + 1;

      grievances.push({
        id: item.grievance_id.slice(0, 6) + "...",
        category,
        status: normalizedStatus,
        date: new Date(item.grievance_submission_timestamp).toLocaleDateString(
          "en-IN",
        ),
      });
    });

    // Backend percentage summary
    const statusSummary =
      Array.isArray(dashboardData.summary) && dashboardData.summary.length
        ? (() => {
          const aggregated = {};
          dashboardData.summary.forEach((item) => {
            const normalizedStatus = item.name;
            if (!aggregated[normalizedStatus]) {
              aggregated[normalizedStatus] = {
                name: normalizedStatus,
                value: 0,
              };
            }
            aggregated[normalizedStatus].value = item.count;
          });
          return Object.values(aggregated).map((item) => ({
            ...item,
          }));
        })()
        : null;

    return {
      total: data.length,

      statusPieData: statusSummary,

      categories: Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      })),

      grievances,

      allGrievances: data,

      overdueGrievances: overdueData,
    };
  }, [data, dashboardData, overdueData]);

  const filteredGrievances = useMemo(() => {
    let filtered = stats.allGrievances;
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        // Filter only by grievance ID as per request
        return (
          item.grievance_id &&
          item.grievance_id.toLowerCase().includes(lowerQuery)
        );
      });
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "grievance_id":
            aValue = a.grievance_id || "";
            bValue = b.grievance_id || "";
            break;
          case "severity":
            aValue = a.severity || "";
            bValue = b.severity || "";
            break;
          case "source":
            aValue = a.source || "";
            bValue = b.source || "";
            break;
          case "reg_date":
            aValue = new Date(a.grievance_submission_timestamp || 0).getTime();
            bValue = new Date(b.grievance_submission_timestamp || 0).getTime();
            break;
          case "tat":
            aValue = a.tat || "";
            bValue = b.tat || "";
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [
    stats.allGrievances,
    stats.overdueGrievances,
    searchQuery,
    searchParam,
    sortConfig,
  ]);

  // Fetch pie chart percentages from the new API
  useEffect(() => {
    const fetchPieChartData = async () => {
      try {
        if (data && data.length > 0) {
          const userId = data[0].user_id;
          if (userId) {
            const res = await axios.get(
              `${AUTH_URL}api/pendingrespondedpercentageuser?user_id=${userId}`,
            );
            if (res.data && res.data.success) {
              setPieChartData({
                pending: {
                  count: res.data.pending_count,
                  percentage: res.data.pending_percentage,
                },
                responded: {
                  count: res.data.responded_count,
                  percentage: res.data.responded_percentage,
                },
                total: res.data.total_grievances,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching pie chart data:", error);
      }
    };

    fetchPieChartData();
  }, [data]);

  const handleViewClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow(res);
      setCommentDraft(res.officer_grievance_comments || res.grievance_response || "");
      setIsViewOnly(true); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(true); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleEditClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow(res);
      setCommentDraft(res.officer_grievance_comments || res.grievance_response || "");
      setIsViewOnly(false); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(false); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
    setCommentDraft("");
    setAtrFile(null);
    setAtrSummary("");
    setAtrFileUrl("");
    setAudioFile(null);
    setAudioSummary("");
    setAudioFileUrl("");
    setBtrMode(null);
    setBtrContent("");
    setBtrFile(null);
    setBtrFileUrl("");
    setBtrSummary("");
    setAiSummaryOpen(false);
    setAiSummaryText("");
  };

  const handleTransfer = () => {
    setTransfer(true);
  };
  const handleTransferAdmin = () => {
    setTransferDept("Admin");
    setTransferAdmin(true);
  };
  const submitTransfer = async (e) => {
    e.preventDefault();
    const payload = {
      query_id: selectedRow._id,
      officer_comments: officerComments,
      transfer_to: tranferDept,
    };

    let res = await axios.post(`${AUTH_URL}api/transfer_grievance`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.status === 200) {
      // The backend (transfer_grievance) now creates the notification for the
      // receiving officer / admin, so no separate reminder call is needed here.
      Swal.fire({
        title: "Complaint Updated!",
        text: "Complaint Transferred Successfully!",
        icon: "success",
      });
      setTransfer(false);
      setTransferAdmin(false);
      setIsModalOpen(false);

      setOfficerComments("");
      setTransferDept("");
    }
  };
  const closeTransfer = () => {
    setTransfer(false);
    setTransferAdmin(false);
    setOfficerComments("");
    setTransferDept("");
  };
  const setTransferDeptartment = (e) => {
    const { value } = e.target;
    setTransferDept(value);
  };

  const handleOfficeComment = (e) => {
    const { value } = e.target;
    setOfficerComments(value);
  };
  // Helper: The update_atr backend only persists atr_summary, not atr_file_url.
  // We embed the file_url at the end of summary using a separator so it survives round-trips.
  const FILE_URL_SEPARATOR = "|||FILE_URL|||";

  const encodeSummaryWithUrl = (summary, fileUrl) => {
    if (!fileUrl) return summary;
    return `${summary}${FILE_URL_SEPARATOR}${fileUrl}`;
  };

  const decodeSummary = (raw) => {
    if (!raw) return { summary: "", fileUrl: "" };
    const parts = raw.split(FILE_URL_SEPARATOR);
    return { summary: parts[0], fileUrl: parts[1] || "" };
  };

  // ── Save ────────────────────────────────────────────────────────────────
  // Saves whatever the officer actually changed in the modal: the comment, a
  // drafted/uploaded BTR that has not been attached yet, a freshly uploaded
  // ATR, or a feedback-call audio. Previously every grievance not in the
  // legacy "Add Comment" status was routed to update_atr, so saving a comment
  // or a BTR always failed with "atr summary is required".
  const handleSave = async () => {
    if (!selectedRow) return;
    const savedComment = (selectedRow.officer_grievance_comments || selectedRow.grievance_response || "").trim();
    const newComment = (commentDraft || "").trim();
    const hasCommentChange = newComment.length > 0 && newComment !== savedComment;
    const hasPendingBtr = Boolean(btrMode) && Boolean((btrContent || "").trim() || (btrMode === "upload" && btrFileUrl));
    const hasPendingAtr = !selectedRow.atr_uploaded && Boolean(atrSummary);
    const hasPendingAudio = Boolean(audioSummary) &&
      (selectedRow.atr_uploaded || selectedRow.officer_grievance_status?.toLowerCase() === "resolved");

    if (!hasCommentChange && !hasPendingBtr && !hasPendingAtr && !hasPendingAudio) {
      Swal.fire({
        icon: "info",
        title: "Nothing to save",
        text: "Add a comment, attach a BTR or upload an ATR before saving.",
      });
      return;
    }

    const headers = { headers: { "Content-Type": "application/json" } };
    const saved = [];
    setLoading(true);
    try {
      if (hasCommentChange) {
        const res = await axios.put(`${AUTH_URL}api/grievance_response`, {
          query_id: selectedRow._id,
          officer_grievance_comments: newComment,
        }, headers);
        setSelectedRow((prev) => ({
          ...prev,
          officer_grievance_comments: newComment,
          grievance_response: newComment,
          user_grievance_status: res.data?.user_grievance_status || prev.user_grievance_status,
        }));
        saved.push("Comment");
      }

      if (hasPendingBtr) {
        await submitBtrRequest(
          btrContent,
          btrMode === "edit" ? (selectedRow.btr_file_url || "") : btrFileUrl,
          btrMode === "upload" ? btrSummary : "",
        );
        setBtrMode(null);
        setBtrContent("");
        saved.push(btrMode === "edit" ? "Revised BTR (sent for admin approval)" : "BTR (sent for admin approval)");
      }

      if (hasPendingAtr) {
        await axios.put(`${AUTH_URL}api/update_atr`, {
          query_id: selectedRow._id,
          summary: encodeSummaryWithUrl(atrSummary, atrFileUrl),
          atr_url: atrFileUrl,
          status: "Resolved",
        }, headers);
        setSelectedRow((prev) => ({
          ...prev,
          atr_uploaded: true,
          atr_summary: atrSummary,
          atr_file_url: atrFileUrl,
          officer_grievance_status: "Resolved",
          user_grievance_status: "Resolved",
        }));
        setAtrFile(null);
        setAtrSummary("");
        setAtrFileUrl("");
        saved.push("ATR");
      }

      if (hasPendingAudio) {
        await axios.put(`${AUTH_URL}api/update_feedback`, {
          query_id: selectedRow._id,
          summary: encodeSummaryWithUrl(
            audioSummary,
            audioFileUrl || decodeSummary(selectedRow.feedback_call_summary).fileUrl,
          ),
          feedback: response,
        }, headers);
        saved.push("Feedback call audio");
      }

      setQueryUpdate(true);
      setIsModalOpen(false);
      Swal.fire({
        icon: "success",
        title: "Saved!",
        html: `<p>${saved.join(", ")} saved successfully.</p>`,
      });
    } catch (error) {
      console.log("Error in Update Query API", error);
      if (saved.length) setQueryUpdate(true);
      // Keep the modal open so the officer does not lose what they entered.
      Swal.fire({
        icon: "error",
        title: "Could not save",
        html: `${saved.length ? `<p>${saved.join(", ")} saved.</p>` : ""}<p>${error?.response?.data?.error || "Something went wrong while saving. Please try again."}</p>`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAtrUpload = async () => {
    if (!atrFile) {
      Swal.fire("Error", "Please select a file to upload", "error");
      return;
    }
    const formData = new FormData();
    formData.append("uploaded_file", atrFile);

    try {
      setAtrUploadLoading(true);
      const res = await axios.post(
        `${CHAT_URL}v1/categorize_atr_file`,
        formData,
        {
          headers: {
            accept: "application/json",
            "x-source": "12",
            "x-consumer-id": "12",
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.status === 200 && res.data) {
        setAtrSummary(stripMarkdownFence(res.data.summary));
        setAtrFileUrl(res.data.file_url);
        Swal.fire({
          title: "Success!",
          text: "ATR Document uploaded and analyzed successfully.",
          icon: "success",
        });
      }
    } catch (error) {
      console.log("Error uploading ATR:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Could not upload the ATR document.",
      });
    } finally {
      setAtrUploadLoading(false);
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile) {
      Swal.fire("Error", "Please select an audio file to upload", "error");
      return;
    }
    const formData = new FormData();
    formData.append("uploaded_file", audioFile);

    try {
      setAudioUploadLoading(true);
      const res = await axios.post(`${CHAT_URL}v1/upload_file`, formData, {
        headers: {
          accept: "application/json",
          "x-source": "787",
          "x-consumer-id": "878",
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 200 && res.data) {
        setAudioSummary(res.data.summary);
        setAudioFileUrl(res.data.file_url);
        setResponse(res.data.response);
        Swal.fire({
          title: "Success!",
          text: "Audio file uploaded and analyzed successfully.",
          icon: "success",
        });
      }
    } catch (error) {
      console.log("Error uploading audio:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Could not upload the audio file.",
      });
    } finally {
      setAudioUploadLoading(false);
    }
  };

  const handleATR = async (grievanceId) => {
    // Get file URL from state (fresh upload) or from persisted summary
    const fileUrl =
      atrFileUrl ||
      decodeSummary(selectedRow?.atr_summary).fileUrl ||
      selectedRow?.atr_file_url;
    if (!fileUrl) {
      Swal.fire({
        icon: "info",
        title: "No ATR File",
        text: "No ATR file URL is available for download.",
      });
      return;
    }

    try {
      // Fetch the file as a blob to force download (cross-origin URLs ignore download attribute)
      // const response = await fetch(fileUrl);
      // const blob = await response.blob();
      // const blobUrl = URL.createObjectURL(blob);
      // const link = document.createElement("a");
      // link.href = blobUrl;
      // link.download = `ATR_${grievanceId || "Document"}.pdf`;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // URL.revokeObjectURL(blobUrl);
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.log("Error downloading ATR:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download the ATR document.",
      });
    }
  };

  const handleAudioDownload = async () => {
    if (!audioFileUrl) {
      Swal.fire({
        icon: "info",
        title: "No Audio File",
        text: "No audio file URL is available for download.",
      });
      return;
    }

    try {
      const response = await fetch(audioFileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "audio download.wav";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.log("Error downloading audio:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download the audio file.",
      });
    }
  };

  const handleLikeClick = (e) => {
    e.preventDefault();
    setIsLiked(true);
    setShowCommentArea(false); // Hide comment area after liking
  };

  const handleDislikeClick = (e) => {
    e.preventDefault();
    setIsLiked(false);
    setShowCommentArea(true); // Show comment area after disliking
  };

  const handleSave1 = async () => {
    setSaveLoading(true);
    try {
      const feedbackData = {
        grievance_id: selectedRow.id,
        feedbackrating: isLiked ? 1 : 0,
        feedbacktext: comment,
      };

      const res = await axios(`${AUTH_URL}/api/feedback`, feedbackData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.status === 200) {
        console.log("Feedback saved successfully");
        setIsLiked(false);
        setComment("");
        setShowCommentArea(false);
      }
    } catch (error) {
      console.error("Error saving feedback:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  // Helper to strip ```markdown ... ``` code fence wrappers
  const stripMarkdownFence = (text) => {
    if (!text) return "";
    return text
      .replace(/^```(?:markdown|md)?\s*\n?/i, "")
      .replace(/\n?```\s*$/i, "")
      .trim();
  };

  // ── BTR Handlers ─────────────────────────────────────────────────────────
  const handleGenerateBtr = async () => {
    setBtrLoading(true);
    setBtrMode("generate");
    try {
      // 1. Get grievance data from backend
      const dataRes = await axios.post(`${AUTH_URL}api/generate_btr`, {
        grievance_id: selectedRow.grievance_id,
      }, { headers: { "Content-Type": "application/json" } });

      const gData = dataRes.data.grievance_data;
      // 2. Ask LLM service to generate BTR text
      const llmRes = await axios.post(`${CHAT_URL}v1/generate_btr_content`, gData, {
        headers: { "Content-Type": "application/json" },
      });
      setBtrContent(stripMarkdownFence(llmRes.data.btr_text || ""));
    } catch (err) {
      console.error("BTR generation error:", err);
      Swal.fire({ icon: "error", title: "BTR Generation Failed", text: "Could not generate BTR. Please try again." });
    } finally {
      setBtrLoading(false);
    }
  };

  const handleBtrFileUpload = async () => {
    if (!btrFile) return;
    setBtrLoading(true);
    const formData = new FormData();
    formData.append("uploaded_file", btrFile);
    try {
      const res = await axios.post(`${CHAT_URL}v1/upload_btr_file`, formData, {
        headers: {
          accept: "application/json",
          "x-source": "12",
          "x-consumer-id": "12",
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.status === 200 && res.data) {
        const cleanSummary = stripMarkdownFence(res.data.summary || "");
        setBtrFileUrl(res.data.file_url || "");
        setBtrSummary(cleanSummary);
        setBtrContent(cleanSummary); // use summary as editable content
        Swal.fire({ icon: "success", title: "BTR Uploaded", text: "BTR PDF uploaded successfully." });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: "Could not upload BTR PDF." });
    } finally {
      setBtrLoading(false);
    }
  };

  // Single place that calls submit_btr and syncs local state.
  const submitBtrRequest = async (content, fileUrl, summary) => {
    const res = await axios.post(`${AUTH_URL}api/submit_btr`, {
      query_id: selectedRow._id || selectedRow.grievance_id,
      btr_content: content || "",
      btr_file_url: fileUrl || "",
      // Empty summary → backend derives it from the (new) content. Never send
      // the previous summary: on resubmission the admin would see stale text.
      btr_summary: summary || "",
    }, { headers: { "Content-Type": "application/json" } });

    setSelectedRow((prev) => ({
      ...prev,
      btr_content: content,
      btr_file_url: fileUrl || "",
      btr_summary: summary || (content || "").slice(0, 500),
      btr_status: "Under Review",
      btr_admin_comment: "",
      btr_timestamp: res.data?.btr_timestamp || new Date().toISOString(),
      btr_reference_no: res.data?.btr_reference_no || prev.btr_reference_no,
      officer_grievance_status: "BTR Under Review",
      user_grievance_status: "InProgress",
    }));
    return res;
  };

  const handleSubmitBtr = async () => {
    if (!btrContent && !btrFileUrl) {
      Swal.fire("Error", "Please generate or upload a BTR before submitting.", "error");
      return;
    }
    setBtrSubmitLoading(true);
    try {
      await submitBtrRequest(btrContent, btrFileUrl, btrSummary);
      setBtrMode(null);
      setBtrContent("");
      Swal.fire({
        icon: "success",
        title: "BTR Submitted!",
        html: `<p>BTR document has been sent to Admin for review successfully.</p>`,
      });
      setQueryUpdate(true);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Submission Failed", text: err?.response?.data?.error || "Could not submit BTR." });
    } finally {
      setBtrSubmitLoading(false);
    }
  };

  // ── Send existing / revised BTR for Admin Approval ────────────────────────
  // overrideContent is the revised text when resubmitting after a rejection.
  // Guard against being wired directly as an onClick handler, which passes the
  // click event in as the first argument.
  const handleSendBtrForApproval = async (overrideContent = null) => {
    const revised = typeof overrideContent === "string" ? overrideContent : null;
    const btrText = revised || selectedRow.btr_content || selectedRow.btr_summary;
    if (!btrText && !selectedRow.btr_file_url) {
      Swal.fire("Error", "No BTR content found. Please generate or upload a BTR first.", "error");
      return false;
    }
    const confirm = await Swal.fire({
      title: revised ? "Resubmit revised BTR?" : "Send BTR for Admin Approval?",
      text: `This will send the BTR for complaint ${selectedRow.grievance_id} to the admin for review.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: revised ? "Yes, Resubmit" : "Yes, Send for Approval",
      confirmButtonColor: "#4f46e5",
    });
    if (!confirm.isConfirmed) return false;

    setBtrSubmitLoading(true);
    try {
      await submitBtrRequest(
        btrText || "",
        selectedRow.btr_file_url || "",
        revised ? "" : (selectedRow.btr_summary || ""),
      );
      Swal.fire({
        icon: "success",
        title: revised ? "BTR Resubmitted!" : "BTR Sent for Approval!",
        html: `<p>The admin has been notified and will review your BTR shortly.</p>`,
      });
      setQueryUpdate(true);
      return true;
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err?.response?.data?.error || "Could not send BTR for approval." });
      return false;
    } finally {
      setBtrSubmitLoading(false);
    }
  };

  // ── AI Summary Handler ────────────────────────────────────────────────────
  const handleAiSummary = async () => {
    setAiSummaryOpen(true);
    setAiSummaryLoading(true);
    setAiSummaryText("");
    try {
      const res = await axios.post(`${CHAT_URL}v1/generate_ai_summary`, {
        grievance_id: selectedRow.grievance_id,
        role: "officer",
      }, { headers: { "Content-Type": "application/json" } });
      setAiSummaryText(res.data.summary || "No summary available.");
    } catch (err) {
      setAiSummaryText("Could not generate summary. Please try again.");
    } finally {
      setAiSummaryLoading(false);
    }
  };

  return (
    <div className="view-inner" style={{ padding: "20px 28px 32px" }}>
      <h2 className="page-title" id="officer-page-title" style={{ fontSize: "20px", fontWeight: "700", color: "var(--brand-dark)", marginBottom: "20px" }}>Assigned Complaints — Complaint & Investigation Cell</h2>
      
      <div className="stat-cards">
        <div className="stat-card c1">
          <div className="top-row"><span>Total User Complaints Registered</span>
            <div className="icon-circle"><i className="fa-regular fa-circle-question"></i></div>
          </div>
          <div className="stat-num" id="officer-stat-total">{stats.total}</div>
        </div>
        <div className="stat-card c2">
          <div className="top-row"><span>Number of User Complaints Pending</span>
            <div className="icon-circle"><i className="fa-solid fa-hourglass-half"></i></div>
          </div>
          <div className="stat-num" id="officer-stat-pending">{dashboardData?.pending || 0}</div>
        </div>
        <div className="stat-card c3">
          <div className="top-row"><span>Number of User Complaints Closed</span>
            <div className="icon-circle"><i className="fa-solid fa-circle-check"></i></div>
          </div>
          <div className="stat-num" id="officer-stat-resolved">{dashboardData?.closed || 0}</div>
        </div>
      </div>

      <div className="grid-2b" style={{ gridTemplateColumns: "260px 1fr", marginTop: "20px" }}>
        <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <h3 style={{ alignSelf: "flex-start", marginBottom: "8px", fontWeight: "700", fontSize: "16px", color: "#0e2f4d" }}>User Satisfaction</h3>
          <div style={{ position: "relative", width: "200px", height: "120px", margin: "10px auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <svg width="190" height="100" viewBox="0 0 200 110">
              <defs>
                <linearGradient id="gaugeGradOff" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#e0433a" />
                  <stop offset="50%" stopColor="#f0a63c" />
                  <stop offset="100%" stopColor="#159a5c" />
                </linearGradient>
              </defs>
              <path d="M 20 90 A 80 80 0 0 1 180 90" fill="none" stroke="url(#gaugeGradOff)" strokeWidth="18" strokeLinecap="round" />
              <circle cx="100" cy="90" r="7" fill="#0e2f4d" />
              <line x1="100" y1="90" x2="172" y2="86" stroke="#0e2f4d" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#0e2f4d", marginTop: "-6px" }}>5/5</div>
          </div>
        </div>
        <div className="card officer-overdue-card">
          <h3 style={{ fontWeight: "700", fontSize: "16px", color: "#0e2f4d", marginBottom: "16px" }}>Overdue Complaints</h3>
          <div className="chart-scroll-wrap" style={{ maxHeight: "240px", overflowY: "auto" }}>
            <table className="data-table" id="officer-overdue-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Complaint ID</th>
                  <th>User Complaint</th>
                  <th>Reg. Date</th>
                  <th>Days Overdue</th>
                </tr>
              </thead>
              <tbody id="officer-overdue-body">
                {overdueData && overdueData.length > 0 ? overdueData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="grid-id"><TruncatedText text={row.grievance_id} limit={20} /></td>
                    <td><TruncatedText text={row.user_query} limit={40} /></td>
                    <td>{new Date(row.grievance_submission_timestamp).toLocaleDateString("en-IN")}</td>
                    <td style={{ color: "var(--orange-warn)", fontWeight: "bold" }}>{row.days_overdue || calculateDaysLapsed(row) || 4}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" style={{ textAlign: "center", padding: "16px" }}>No overdue complaints found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: "20px", marginBottom: "20px" }}>
        <h3 style={{ fontWeight: "700", fontSize: "16px", color: "#0e2f4d", marginBottom: "16px" }}>Complaints Over Time</h3>
        <div className="officer-toolbar" style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <input 
            className="search-bar" 
            id="officer-search" 
            placeholder="Search by Complaint ID or citizen name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
          <select 
            className="status-filter" 
            id="officer-status-filter"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Open">Open</option>
            <option value="Resolved">Resolved</option>
          </select>
        </div>
        <div className="chart-scroll-wrap" style={{ maxHeight: "340px", overflowY: "auto" }}>
          <table className="data-table" id="officer-main-table">
            <thead>
              <tr>
                <th style={{ width: "44px" }}>S.No.</th>
                <th className="sortable cursor-pointer" onClick={() => requestSort("grievance_id")}>Complaint ID <i className="fa-solid fa-sort"></i></th>
                <th>User Complaint</th>
                <th className="sortable cursor-pointer">Department <i className="fa-solid fa-sort"></i></th>
                <th className="sortable cursor-pointer">Category <i className="fa-solid fa-sort"></i></th>
                <th className="sortable cursor-pointer">Status <i className="fa-solid fa-sort"></i></th>
                <th>Officer Response</th>
                <th>ATR</th>
                <th>BTR</th>
                <th className="sortable cursor-pointer" onClick={() => requestSort("reg_date")}>Reg. Date <i className="fa-solid fa-sort"></i></th>
                <th className="sortable cursor-pointer">TAT <i className="fa-solid fa-sort"></i></th>
              </tr>
            </thead>
            <tbody id="officer-table-body">
              {(() => {
                let displayedData = data || [];
                if (searchParam !== "All") {
                  displayedData = displayedData.filter(row => getOfficerGrievanceStatus(row) === searchParam || (searchParam === "Open" && getOfficerGrievanceStatus(row) !== "Resolved"));
                }
                if (searchQuery) {
                  const lower = searchQuery.toLowerCase();
                  displayedData = displayedData.filter(row => row.grievance_id?.toLowerCase().includes(lower) || row.user_name?.toLowerCase().includes(lower));
                }
                
                if (sortConfig.key) {
                  displayedData = [...displayedData].sort((a, b) => {
                    let aValue, bValue;
                    if (sortConfig.key === "grievance_id") {
                      aValue = a.grievance_id || "";
                      bValue = b.grievance_id || "";
                    } else if (sortConfig.key === "reg_date") {
                      aValue = new Date(a.grievance_submission_timestamp || 0).getTime();
                      bValue = new Date(b.grievance_submission_timestamp || 0).getTime();
                    }
                    if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
                    if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
                    return 0;
                  });
                }
                
                return displayedData.map((row, idx) => {
                  const status = getOfficerGrievanceStatus(row) || "Open";
                  return (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td className="grid-id"><TruncatedText text={row.grievance_id} limit={16} /></td>
                      <td><TruncatedText text={row.user_query} limit={30} /></td>
                      <td>
                        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', minWidth: '150px' }}>
                          Ministry of Women and Child Development
                        </div>
                      </td>
                      <td>
                        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', minWidth: '150px' }}>
                          {row.llm_subcategory || "Other"}
                        </div>
                      </td>
                       <td>
                         {status === 'Resolved' ? (
                           <span className="status-badge resolved" style={{ cursor: 'pointer' }} onClick={() => handleViewClick(row)}>Resolved</span>
                         ) : status === 'BTR Under Review' ? (
                           <span style={{ display:'inline-block', padding:'4px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#fef3c7', color:'#92400e', border:'1px solid #fcd34d', cursor: 'pointer' }} onClick={() => handleViewClick(row)}>
                             ⏳ BTR Under Review
                           </span>
                         ) : status === 'BTR Rejected' ? (
                           <span style={{ display:'inline-block', padding:'4px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#fee2e2', color:'#991b1b', border:'1px solid #fca5a5', cursor: 'pointer' }} onClick={() => handleViewClick(row)}>
                             ✗ BTR Rejected
                           </span>
                         ) : status === 'Attach ATR' ? (
                           <span style={{ display:'inline-flex', alignItems: 'center', justifyContent: 'center', padding:'4px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#dbeafe', color:'#1e40af', border:'1px solid #93c5fd', cursor: 'pointer' }} onClick={() => handleViewClick(row)}>
                             📎 Attach ATR
                           </span>
                         ) : status === 'Attach BTR' ? (
                           <button className="btn-action" style={{ display:'inline-block', padding:'4px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#e0f2fe', color:'#0369a1', border:'1px solid #7dd3fc', cursor: 'pointer' }} onClick={() => handleViewClick(row)}>
                             📝 Attach BTR
                           </button>
                         ) : (
                           <span style={{ display:'inline-block', padding:'4px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', cursor: 'pointer' }} onClick={() => handleViewClick(row)}>
                             {status}
                           </span>
                         )}
                       </td>
                      <td><TruncatedText text={row.officer_grievance_comments || row.grievance_response || "—"} limit={20} /></td>
                      <td><TruncatedText text={stripMarkdown(row.atr_summary ? decodeSummary(row.atr_summary)?.summary || row.atr_summary : row.atr_content || "—")} limit={20} /></td>
                      <td><TruncatedText text={stripMarkdown(row.btr_summary ? decodeSummary(row.btr_summary)?.summary || row.btr_summary : row.btr_content || "—")} limit={20} /></td>
                      <td>{new Date(row.grievance_submission_timestamp).toLocaleDateString("en-IN")}</td>
                      <td>{row.tat || "3"}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

              {/* Modal */}
        {isModalOpen && selectedRow && createPortal(
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 sm:p-8">
              <div className="bg-white w-full sm:w-[80vw] max-w-4xl p-5 sm:p-6 rounded shadow-lg relative my-8">
              <h3 className="text-lg font-bold mb-4">Complaint Summary</h3>
              <form className="space-y-4">
                {/* Query Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    User Complaint
                  </label>
                  <input
                    type="text"
                    name="user_query"
                    value={selectedRow.user_query}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bot Response
                  </label>
                  <textarea
                    name="llm_response_desc"
                    value={selectedRow.llm_response_desc}
                    // onChange={handleInputChange}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                {selectedRow?.officer_transfer_comments?.trim() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer Comment from Officer
                    </label>
                    <textarea
                      name="officer_grievance_comments"
                      value={
                        selectedRow?.officer_transfer_comments?.trim() || ""
                      }
                      readOnly={true}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                      rows={3} // You can adjust the number of rows as per your requirement
                    ></textarea>
                  </div>
                )}
                {/* Response from expert */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    name="officer_grievance_comments"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Add your comment and click Save"
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ministry
                  </label>
                  <input
                    type="text"
                    name="llm_ministry"
                    value={selectedRow.llm_ministry}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <input
                    type="text"
                    name="llm_category"
                    value={selectedRow.llm_category}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Sub Category
                  </label>
                  <input
                    type="text"
                    name="llm_subcategory"
                    value={selectedRow.llm_subcategory}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={selectedRow.state || ""}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    District
                  </label>
                  <input
                    type="text"
                    name="district"
                    value={selectedRow.district}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Severity
                  </label>
                  <input
                    type="text"
                    name="severity"
                    value={selectedRow.severity}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    User Id
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    value={selectedRow.user_id}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                  />
                </div>

                {/* SECTION 1: Brief Transmission Report (BTR) — Always displayed first */}
                <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-200 mt-4 shadow-xs">
                  <h4 className="text-md font-bold text-indigo-900 mb-3 flex items-center gap-1.5">
                    📋 Brief Transmission Report (BTR)
                  </h4>

                  {/* If BTR is already Attached OR status is 'Attach ATR' / 'Resolved' */}
                  {(selectedRow.btr_status === "Attached" ||
                    (selectedRow.btr_status && selectedRow.btr_status !== "None") ||
                    selectedRow.btr_file_url ||
                    selectedRow.btr_content ||
                    selectedRow.btr_summary ||
                    getOfficerGrievanceStatus(selectedRow) === "Attach ATR" ||
                    getOfficerGrievanceStatus(selectedRow) === "Resolved" ||
                    selectedRow.atr_uploaded) ? (
                    <div>
                      {/* Dynamic BTR Status Banner */}
                      <div className={`flex items-center justify-between p-3 rounded-lg border mb-3 ${
                        selectedRow.btr_status === "Approved"
                          ? "bg-green-50 border-green-200"
                          : selectedRow.btr_status === "Rejected"
                          ? "bg-red-50 border-red-200"
                          : "bg-amber-50 border-amber-200"
                      }`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          {selectedRow.btr_status === "Approved" ? (
                            <span className="px-2.5 py-1 bg-green-100 text-green-800 font-bold text-xs rounded-full flex items-center gap-1">
                              &#10003; BTR Approved by Admin
                            </span>
                          ) : selectedRow.btr_status === "Rejected" ? (
                            <span className="px-2.5 py-1 bg-red-100 text-red-800 font-bold text-xs rounded-full flex items-center gap-1">
                              &#10007; Revision Requested by Admin
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 bg-amber-100 text-amber-800 font-bold text-xs rounded-full flex items-center gap-1">
                              &#9203; BTR Under Review (Awaiting Admin Approval)
                            </span>
                          )}
                          {selectedRow.btr_timestamp && (
                            <span className="text-xs text-slate-500">
                              Submitted on {new Date(selectedRow.btr_timestamp).toLocaleDateString("en-IN")}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Admin rejection reason */}
                      {selectedRow.btr_status === "Rejected" && selectedRow.btr_admin_comment && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs font-bold text-red-800 uppercase mb-1">Admin's Revision Note:</p>
                          <p className="text-sm text-red-700">{selectedRow.btr_admin_comment}</p>
                        </div>
                      )}

                      {/* Prefilled BTR Summary / Content Display — hidden while editing */}
                      {btrMode !== "edit" && (
                        <div className="mt-3 bg-white p-3 rounded-lg border border-indigo-100">
                          <label className="block text-xs font-bold text-indigo-900 uppercase mb-1">
                            BTR Summary / Case Scope
                          </label>
                          <FormattedMarkdown
                            content={
                              getBtrText(selectedRow) ||
                              `**Brief Transmission Report (BTR)** was attached for Police Escalation for grievance **${selectedRow.grievance_id || ""}**.`
                            }
                          />
                        </div>
                      )}

                      {/* BTR Actions */}
                      {(selectedRow.btr_file_url || btrFileUrl || selectedRow.btr_content || selectedRow.btr_summary) && (
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                          {/* View / Download */}
                          {getBtrText(selectedRow) && (
                            <button
                              type="button"
                              onClick={() => downloadBtrPdf(selectedRow)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs rounded-md shadow-xs transition-colors cursor-pointer"
                            >
                              📄 Download BTR (PDF)
                            </button>
                          )}
                          {(selectedRow.btr_file_url || btrFileUrl) && (
                            <button
                              type="button"
                              onClick={() => window.open(selectedRow.btr_file_url || btrFileUrl, "_blank", "noopener")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white font-medium text-xs rounded-md shadow-xs transition-colors cursor-pointer"
                            >
                              📎 Open Uploaded BTR File
                            </button>
                          )}

                          {/* BTR Rejected: Only show Edit BTR — Resubmit is inside the edit flow */}
                          {selectedRow.btr_status === "Rejected" && (
                            <div className="ml-auto flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setBtrContent((selectedRow.btr_content || selectedRow.btr_summary || "").replace(/[-*]?\s*\**Reference No:\**\s*\[Auto-numbered by system\]\s*\n?/gi, ""));
                                  setBtrTab("edit");
                                  setBtrMode("edit");
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-xs rounded-md shadow-sm transition-colors cursor-pointer"
                              >
                                <i className="fa-solid fa-pen text-[10px]"></i>
                                ✏️ Edit &amp; Resubmit BTR
                              </button>
                            </div>
                          )}
                          {/* Not yet submitted (Attached): show Send for Approval */}
                          {selectedRow.btr_status !== "Under Review" && selectedRow.btr_status !== "Approved" && selectedRow.btr_status !== "Rejected" && (
                            <div className="ml-auto flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleSendBtrForApproval()}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-md shadow-sm transition-colors cursor-pointer"
                              >
                                <i className="fa-solid fa-paper-plane text-[10px]"></i>
                                Send for BTR Approval
                              </button>
                            </div>
                          )}

                          {selectedRow.btr_status === "Under Review" && (
                            <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 font-bold text-xs rounded-md border border-amber-200">
                              ⏳ Awaiting Admin Approval
                            </span>
                          )}
                          {selectedRow.btr_status === "Approved" && (
                            <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 font-bold text-xs rounded-md border border-green-200">
                              ✓ BTR Approved
                            </span>
                          )}
                        </div>
                      )}

                      {/* Inline BTR Editor (shown when officer clicks Edit BTR) */}
                      {btrMode === "edit" && (
                        <div className="mt-4 bg-white p-3.5 rounded-xl border-2 border-yellow-400">
                          <div className="flex flex-wrap items-center justify-between border-b pb-2 mb-3 gap-2">
                            <label className="text-xs font-bold text-yellow-800 uppercase tracking-wide">
                              ✏️ Editing BTR Document
                            </label>
                            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                              <button type="button"
                                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${btrTab === "preview" ? "bg-white text-indigo-900" : "text-slate-600"}`}
                                onClick={() => setBtrTab("preview")}>
                                ✨ Preview
                              </button>
                              <button type="button"
                                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${btrTab === "edit" ? "bg-white text-indigo-900" : "text-slate-600"}`}
                                onClick={() => setBtrTab("edit")}>
                                ✏️ Edit Raw
                              </button>
                            </div>
                          </div>
                          {btrTab === "preview" ? (
                            <div className="p-3 bg-slate-50 rounded-lg border max-h-80 overflow-y-auto">
                              <FormattedMarkdown content={btrContent} />
                            </div>
                          ) : (
                            <textarea
                              value={btrContent}
                              onChange={(e) => setBtrContent(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-xs sm:text-sm font-mono focus:outline-none focus:ring focus:ring-yellow-300"
                              rows={10}
                              autoFocus
                            />
                          )}
                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-semibold disabled:opacity-50"
                              disabled={btrSubmitLoading || !btrContent.trim()}
                              onClick={async () => {
                                const ok = await handleSendBtrForApproval(btrContent);
                                if (ok) {
                                  setBtrMode(null);
                                  setBtrContent("");
                                }
                              }}
                            >
                              {btrSubmitLoading ? "Sending..." : "💾 Save & Resubmit"}
                            </button>
                            <button
                              type="button"
                              className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-xs"
                              onClick={() => { setBtrMode(null); setBtrContent(""); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Create / Attach New BTR (Only when status is Attach BTR) */
                    <>
                      {!btrMode && (
                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1 shadow-xs"
                            onClick={handleGenerateBtr}
                            disabled={btrLoading}
                          >
                            {btrLoading ? "Generating..." : "✨ Generate BTR (AI)"}
                          </button>
                          <button
                            type="button"
                            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium shadow-xs"
                            onClick={() => setBtrMode("upload")}
                          >
                            📁 Upload BTR PDF
                          </button>
                        </div>
                      )}

                      {/* Upload BTR PDF Mode */}
                      {btrMode === "upload" && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-3 bg-white p-3 rounded-lg border">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setBtrFile(e.target.files[0])}
                            className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          />
                          <button
                            type="button"
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 shadow-xs"
                            onClick={handleBtrFileUpload}
                            disabled={btrLoading || !btrFile}
                          >
                            {btrLoading ? "Uploading..." : "Upload BTR"}
                          </button>
                        </div>
                      )}

                      {/* Editable BTR Text & Formatted Preview */}
                      {btrContent && (
                        <div className="mt-3 bg-white p-3.5 rounded-xl border border-indigo-100 shadow-xs">
                          <div className="flex flex-wrap items-center justify-between border-b pb-2 mb-3 gap-2">
                            <label className="text-xs font-bold text-indigo-950 uppercase tracking-wide">
                              Brief Transmission Report (BTR) Document
                            </label>
                            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                              <button
                                type="button"
                                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${btrTab === "preview"
                                  ? "bg-white text-indigo-900 shadow-xs"
                                  : "text-slate-600 hover:text-slate-900"
                                  }`}
                                onClick={() => setBtrTab("preview")}
                              >
                                ✨ Formatted Preview
                              </button>
                              <button
                                type="button"
                                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${btrTab === "edit"
                                  ? "bg-white text-indigo-900 shadow-xs"
                                  : "text-slate-600 hover:text-slate-900"
                                  }`}
                                onClick={() => setBtrTab("edit")}
                              >
                                ✏️ Edit Raw Text
                              </button>
                            </div>
                          </div>

                          {btrTab === "preview" ? (
                            <div className="p-3 bg-slate-50/50 rounded-lg border border-slate-200 max-h-80 overflow-y-auto">
                              <FormattedMarkdown content={btrContent} />
                            </div>
                          ) : (
                            <textarea
                              value={btrContent}
                              onChange={(e) => setBtrContent(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-xs sm:text-sm font-mono focus:outline-none focus:ring focus:ring-indigo-300"
                              rows={10}
                            />
                          )}

                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-semibold shadow-xs disabled:opacity-50"
                              onClick={handleSubmitBtr}
                              disabled={btrSubmitLoading}
                            >
                              {btrSubmitLoading ? "Attaching..." : "Attach BTR"}
                            </button>
                            <button
                              type="button"
                              className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 text-xs font-medium"
                              onClick={() => {
                                setBtrMode(null);
                                setBtrContent("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* SECTION 2: Action Taken Report (ATR) — Shown only when status is Attach ATR and BTR is approved, or Resolved */}
                {((getOfficerGrievanceStatus(selectedRow) === "Attach ATR" && selectedRow.btr_status === "Approved") ||
                  getOfficerGrievanceStatus(selectedRow) === "Resolved" ||
                  selectedRow.atr_uploaded) && (
                  <>
                    {/* If ATR is already uploaded or status is Resolved — prefilled non-editable ATR card */}
                    {getOfficerGrievanceStatus(selectedRow) === "Resolved" || selectedRow.atr_uploaded ? (
                      <div className="bg-green-50/70 p-4 rounded-xl border border-green-200 mt-4 shadow-xs">
                        <h4 className="text-md font-bold text-green-900 mb-2 flex items-center gap-1.5">
                          ✅ Action Taken Report (ATR)
                        </h4>
                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-green-100 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-1 bg-green-100 text-green-800 font-semibold text-xs rounded-full">
                              ATR Uploaded
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 bg-white p-3 rounded-lg border border-green-100">
                          <label className="block text-xs font-bold text-green-900 uppercase mb-1">
                            ATR Summary
                          </label>
                          <FormattedMarkdown
                            content={
                              decodeSummary(selectedRow.atr_summary).summary ||
                              selectedRow.atr_summary ||
                              "**Action Taken Report (ATR)** has been uploaded and resolved."
                            }
                          />
                        </div>

                        {(selectedRow.atr_file_url || decodeSummary(selectedRow?.atr_summary).fileUrl) && (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs rounded-md shadow-xs transition-colors"
                              onClick={() => handleATR(selectedRow.grievance_id)}
                            >
                              📄 Download ATR PDF
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Active ATR Management (When status is Attach ATR) */
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mt-4 shadow-xs">
                        <h4 className="text-md font-bold text-gray-800 mb-2 flex items-center gap-1.5">
                          📄 Action Taken Report (ATR) Management
                        </h4>

                        {!decodeSummary(selectedRow?.atr_summary).summary && !atrSummary && (
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-4">
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) => setAtrFile(e.target.files[0])}
                              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-green-300 text-sm"
                            />
                            <button
                              type="button"
                              className="px-4 py-2 w-full sm:w-auto bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50 shadow-xs"
                              onClick={handleAtrUpload}
                              disabled={atrUploadLoading || !atrFile}
                            >
                              {atrUploadLoading ? "Uploading..." : "Upload ATR"}
                            </button>
                          </div>
                        )}

                        {(atrSummary || decodeSummary(selectedRow?.atr_summary).summary) && (
                          <div className="mt-2">
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                              ATR Summary
                            </label>
                            <div className="w-full px-3 py-3 border rounded-lg bg-white text-gray-700 text-sm max-h-64 overflow-y-auto shadow-inner">
                              <FormattedMarkdown
                                content={atrSummary || decodeSummary(selectedRow?.atr_summary).summary}
                              />
                            </div>
                          </div>
                        )}

                        {!atrSummary && (selectedRow.atr_file_url || (!atrFileUrl && decodeSummary(selectedRow?.atr_summary).fileUrl)) && (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md shadow-xs"
                              onClick={() => handleATR(selectedRow.grievance_id)}
                            >
                              📄 Download ATR
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Audio Upload Section - only show after ATR summary is available */}
                {selectedRow?.officer_grievance_status?.toLowerCase() ===
                  "resolved" &&
                  (atrSummary ||
                    decodeSummary(selectedRow?.atr_summary).summary) && (
                    <div className="bg-gray-50 p-4 rounded-lg border mt-4">
                      <h4 className="text-md font-semibold text-gray-800 mb-2">
                        Audio Upload
                      </h4>
                      {!(
                        selectedRow.feedback_call_uploaded &&
                        selectedRow.feedback_call_summary
                      ) && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Audio File
                            </label>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                              <input
                                type="file"
                                accept="audio/*"
                                onChange={(e) => setAudioFile(e.target.files[0])}
                                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-green-300 text-sm"
                              />
                              <button
                                type="button"
                                className="px-4 py-2 w-full sm:w-auto bg-green-500 text-white rounded hover:bg-green-600 font-medium disabled:opacity-50"
                                onClick={handleAudioUpload}
                                disabled={audioUploadLoading || !audioFile}
                              >
                                {audioUploadLoading
                                  ? "Uploading..."
                                  : "Upload Audio"}
                              </button>
                            </div>
                          </div>
                        )}
                      {(audioSummary ||
                        decodeSummary(selectedRow?.feedback_call_summary).summary) && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Audio Summary
                            </label>
                            <div className="w-full px-3 py-3 border rounded-lg bg-white text-gray-700 text-sm max-h-64 overflow-y-auto shadow-inner">
                              <FormattedMarkdown
                                content={
                                  audioSummary ||
                                  decodeSummary(selectedRow?.feedback_call_summary)
                                    .summary
                                }
                              />
                            </div>
                          </div>
                        )}
                      {/* {audioFileUrl && (
                      <div className="mt-4">
                        <button
                          type="button"
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
                          onClick={handleAudioDownload}
                        >
                          Download Audio
                        </button>
                      </div>
                    )} */}
                    </div>
                  )}

                {/* Buttons */}
                <div className="flex flex-wrap justify-end gap-2 sm:gap-4 pt-4 border-t">
                  {/* AI Summary button — always visible */}
                  <button
                    type="button"
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 whitespace-nowrap flex items-center gap-1 text-sm"
                    onClick={handleAiSummary}
                  >
                    <AutoAwesomeIcon fontSize="small" /> AI Summary
                  </button>
                  {(() => {
                    // Show Transfer/Escalate ONLY when no BTR has been submitted yet
                    const officerStatus = getOfficerGrievanceStatus(selectedRow);
                    const btrSubmitted = selectedRow.btr_status &&
                      selectedRow.btr_status !== "None" &&
                      selectedRow.btr_status?.toLowerCase() !== "none" &&
                      selectedRow.btr_status?.toLowerCase() !== "";
                    return officerStatus !== "Resolved" && !btrSubmitted;
                  })() && (
                    <>
                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                        onClick={handleTransferAdmin}
                      >
                        Escalate to admin
                      </button>

                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                        onClick={handleTransfer}
                      >
                        Transfer to another officer
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}
                  {(selectedRow?.officer_grievance_status?.toLowerCase() !==
                    "resolved" ||
                    atrSummary ||
                    audioSummary ||
                    (commentDraft || "").trim() !== (selectedRow?.officer_grievance_comments || selectedRow?.grievance_response || "").trim()) && (
                      <button
                        type="button"
                        className="px-4 py-2 bg-[#45B07B] text-white rounded hover:bg-[#12A57C] whitespace-nowrap flex-1 sm:flex-none text-center disabled:opacity-60"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>
                    )}
                </div>
              </form>
            </div>
          </div>
        </div>, document.body)}

        {transfer && selectedRow && createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50 p-4 sm:p-0">
            <div className="bg-white w-full sm:w-[50vw] max-w-lg p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto">
              <h3 className="text-lg font-bold mb-4">Transfer Complaint</h3>
              <form className="space-y-4" onSubmit={submitTransfer}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Transfer to
                  </label>
                  <select
                    required
                    onChange={setTransferDeptartment}
                    className={`w-full px-3 py-2 border  "border-gray-300"
                       rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-300`}
                  >
                    <option value="">Select Department</option>
                    {availableDepartments.length > 0 ? (
                      availableDepartments.map((dept, idx) => (
                        <option key={idx} value={dept}>{dept}</option>
                      ))
                    ) : (
                      <option value="" disabled>No other departments available</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    required
                    name="officer_comments"
                    value={officerComments}
                    onChange={handleOfficeComment}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="px-4 py-2 w-full sm:w-auto bg-gray-500 text-white rounded hover:bg-gray-600 text-center"
                    onClick={closeTransfer}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}

                  <button
                    type="submit"
                    className="px-4 py-2 w-full sm:w-auto bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
                  >
                    {loading ? "Transferring..." : "Transfer"}
                  </button>
                </div>
              </form>
            </div>
          </div>, document.body)}
        {transferAdmin && selectedRow && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-0">
            <div className="bg-white w-full sm:w-[50vw] max-w-lg p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto">
              <h3 className="text-lg font-bold mb-4">
                Raise Complaint to Admin
              </h3>
              <form className="space-y-4" onSubmit={submitTransfer}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    required
                    name="officer_comments"
                    value={officerComments}
                    onChange={handleOfficeComment}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="px-4 py-2 w-full sm:w-auto bg-gray-500 text-white rounded hover:bg-gray-600 text-center"
                    onClick={closeTransfer}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}

                  <button
                    type="submit"
                    className="px-4 py-2 w-full sm:w-auto bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
                  >
                    {loading ? "Raising..." : "Raise"}
                  </button>
                </div>
              </form>
            </div>
          </div>, document.body)}

        {/* AI Summary Modal */}
        <AiSummaryModal
          isOpen={aiSummaryOpen}
          onClose={() => setAiSummaryOpen(false)}
          summaryText={aiSummaryText}
          loading={aiSummaryLoading}
          grievanceData={selectedRow}
          role="Officer"
        />
      </div>
  );
}
